/**
 * useNewBuild - has a newer version of the app gone live since this tab
 * loaded? (2026-10-20: a deploy reached nobody whose tab was already open,
 * and nothing told them.)
 *
 * The tab asks the server for index.html - past every cache - once on
 * mount, every `everyMs`, and whenever it comes back into view, and
 * compares the entry script it names with the one this tab loaded
 * (utils/buildEntry). A tab that loaded no built entry (the dev server)
 * never asks. A failed check is no news: it is logged, and the next one
 * tries again. Once true it stays true; the banner's Reload ends it.
 *
 * @param opts.loaded - the entry this tab loaded (`loadedEntry()`), null on the dev server
 * @param opts.fetchImpl - injected for tests; the window's fetch otherwise
 * @param opts.everyMs - how often to ask while the tab stays open (default 5 minutes)
 * @returns true once the server serves a different build
 */
import { useEffect, useState } from 'react';
import { entryOf, isNewBuild } from '../utils/buildEntry';
import { diag, errorSummary } from '../diagnostics/log';

const FIVE_MINUTES = 5 * 60_000;

interface Options {
  loaded: string | null;
  fetchImpl?: typeof fetch;
  everyMs?: number;
}

/** The entry this tab loaded: its own module script, as the build wrote it. */
export function loadedEntry(): string | null {
  const src = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute('src');
  return src ? entryOf(src) : null;
}

export default function useNewBuild({ loaded, fetchImpl = fetch, everyMs = FIVE_MINUTES }: Options): boolean {
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    if (!loaded || newer) return undefined;
    let live = true;
    const check = () => {
      fetchImpl('/index.html', { cache: 'no-store' })
        .then((res) => res.text())
        .then((html) => live && isNewBuild(loaded, entryOf(html)) && setNewer(true))
        .catch((err) => diag.warn('app', 'the new-version check failed', errorSummary(err)));
    };
    const onVisible = () => document.visibilityState === 'visible' && check();
    check();
    const timer = setInterval(check, everyMs);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      live = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loaded, fetchImpl, everyMs, newer]);
  return newer;
}
